<script lang="ts">
	import Title from './title.svelte';
	import PostByline from './PostByline.svelte';
	import PostDigs from './PostDigs.svelte';
	import Share from './share/Share.svelte';
	import { profileStore } from '$lib/user/profile/profile.store';
	import LocationDropdown from './locationDropdown.svelte';
	import { get } from 'svelte/store';
	import { derived } from 'svelte/store';
	import { createEventDispatcher } from 'svelte';

	export let editableTitle: boolean = false;
	export let title: string = '';
	export let author: string = '';
	export let created_at: string | Date | undefined = undefined;
	export let digsCount: number = 0;
	export let digDisabled: boolean = false;
	export let shareSlug: string = '';
	export let shareTitle: string = '';
	export let showDigs: boolean = true;
	export let showShare: boolean = true;

	export let postType: string = '';
	export let location: string = '';

	let showLocationDropdown = false;
	let currentLocation = '';
	let showJoinPrompt = false;

	// Subscribe to profileStore for default location
	$: currentLocation = $profileStore.localbloom || '';

	// Add derived values for user/member status
	const userIsLoggedIn = derived(profileStore, ($p) => !!$p && !!$p.id);
	const userIsMember = derived(profileStore, ($p) => !!$p && !!$p.member);

	const dispatch = createEventDispatcher();

	function handleLocationClick() {
		showLocationDropdown = true;
	}

	function handleLocationChange(newLocation: string) {
		currentLocation = newLocation;
		showLocationDropdown = false;
		// Optionally, update profileStore or postStore here if needed
	}

	// Forward dig event
	function handleDig() {
		const isMember = get(profileStore).member;
		if (!isMember) {
			showJoinPrompt = true;
		} else {
			dispatch('dig');
		}
	}
</script>

<div class="w-full mt-4 mb-2">
	<!-- Header row: title and right-side actions (desktop only) -->
	<div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-2">
		<!-- Title -->
		{#if editableTitle}
			<Title create={true} />
		{:else}
			<h1 class="text-2xl md:text-3xl xl:text-4xl font-bold text-gray-200 font-exo-2">{title}</h1>
		{/if}
		<!-- Right: Digs and Share (desktop only) -->
		<div
			class="hidden sm:flex flex-row items-center gap-6 md:gap-8 pr-2 flex-none my-5 sm:my-0"
			style="min-width:fit-content;"
			class:invisible={!showDigs && !showShare}
		>
			{#if showDigs}
				<PostDigs
					count={digsCount}
					disabled={digDisabled}
					showPrompt={showJoinPrompt}
					on:dig={handleDig}
				>
					<span slot="prompt">
						<slot name="digPrompt">
							<a class="underline text-blue-700" rel="noreferrer" href="/join">Join Bloom</a> to interact
							with posts!
						</slot>
					</span>
				</PostDigs>
			{/if}
			{#if showShare}
				<Share post_slug={shareSlug} title={shareTitle} />
			{/if}
		</div>
	</div>
	<!-- Byline and Location below -->
	<PostByline
		{author}
		{created_at}
		prefix={postType === 'action'
			? 'logged by'
			: postType === 'event' || postType === 'template'
				? 'Posted by'
				: 'by'}
	/>
	<div class="mt-1 flex items-center gap-2">
		{#if postType === 'blog' || postType === 'creative'}
			<span
				class="text-xs sm:text-sm md:text-md 2xl:text-base text-left text-bloomPinkNormal font-exo-2 mt-2 xl:mt-1"
			>
				Beamed to you with love from
			</span>
		{/if}
		{#if editableTitle}
			{#if $userIsLoggedIn && $userIsMember}
				{#if !showLocationDropdown}
					<span
						class="text-xs sm:text-sm md:text-md 2xl:text-base text-left text-gradientBlueMid cursor-pointer underline font-exo-2"
						on:click={handleLocationClick}
					>
						{currentLocation || 'Select location'}
					</span>
				{:else}
					<div class="w-64">
						<LocationDropdown view={false} on:change={(e) => handleLocationChange(e.detail)} />
					</div>
				{/if}
			{:else}
				<span
					class="text-xs sm:text-sm md:text-md 2xl:text-base text-left text-gradientBlueMid font-exo-2"
				>
					{currentLocation}
				</span>
			{/if}
		{:else}
			<span
				class="text-xs sm:text-sm md:text-md 2xl:text-base text-left text-gradientBlueMid font-exo-2"
			>
				{location}
			</span>
		{/if}
	</div>
	<!-- Digs and Share (mobile only) -->
	<div
		class="flex flex-row items-center gap-6 mt-5 sm:hidden"
		style="min-width:fit-content;"
		class:invisible={!showDigs && !showShare}
	>
		{#if showDigs}
			<PostDigs
				count={digsCount}
				disabled={digDisabled}
				showPrompt={showJoinPrompt}
				on:dig={handleDig}
			>
				<span slot="prompt">
					<slot name="digPrompt">
						<a class="underline text-blue-700" rel="noreferrer" href="/join">Join Bloom</a> to interact
						with posts!
					</slot>
				</span>
			</PostDigs>
		{/if}
		{#if showShare}
			<Share post_slug={shareSlug} title={shareTitle} />
		{/if}
	</div>
</div>
